/**
 * Server Data Context
 * Provides pre-fetched Shopify data during SSR to avoid empty loading states.
 * Components check this context first and skip client-side fetching if data exists.
 */

import { createContext, useContext } from 'react';
import type { Product, Collection, PageInfo } from './shopify/types';

export interface ServerData {
  /** The route path this data was pre-rendered for */
  routePath?: string;
  /** Pre-fetched products (for homepage FeaturedProducts and ProductsPage) */
  products?: Product[];
  /** Page info for products pagination */
  productsPageInfo?: PageInfo;
  /** Pre-fetched collections list */
  collections?: Array<{ id: string; handle: string; title: string; description: string; image: { url: string; altText: string | null; width: number; height: number } | null }>;
  /** Pre-fetched collection data keyed by handle (for CollectionPage) */
  collectionData?: Record<string, {
    collection: Collection;
    products: Product[];
    pageInfo: PageInfo;
  }>;
}

const ServerDataContext = createContext<ServerData>({});

export const ServerDataProvider = ServerDataContext.Provider;

export function useServerData(): ServerData {
  return useContext(ServerDataContext);
}
